import { useCallback, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import {
  getAllureMapStyle,
  getInstantMapStyle,
  prefetchAllureMapStyle,
} from '../components/guest-map/mapStyleUtils';

/**
 * Owns the maplibre-gl Map instance lifecycle for the Guest Map:
 *   - one-shot init on mount (instant style → real style swap)
 *   - stable bound-change + click listeners (uses ref pattern so we
 *     don't tear down + rebuild the map every time a callback changes)
 *   - style swap on theme change
 *   - first-time recentre when geolocation resolves AFTER map init
 *   - `centerOnMe` helper (also re-requests permission if not live)
 *
 * Marker cleanup on unmount uses the refs passed in — this hook owns
 * the map, `useGuestMapMarkers` owns the marker DOM.
 */
export function useGuestMapInstance({
  mapCanvasRef,
  mapTheme,
  userPosition,
  defaultCenter,
  locationStatus,
  requestLocation,
  onBoundsChange,
  onMapClick,
  markersRef,
  experienceMarkersRef,
  userMarkerRef,
}) {
  const mapRef = useRef(null);
  const toLngLat = useCallback((coords) => [coords[1], coords[0]], []);
  const getBoundsFromMap = useCallback((map) => {
    const bounds = map.getBounds();
    return {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    };
  }, []);

  // Ref-mirror the caller's handlers so map listeners stay stable
  // across renders. Otherwise every parent re-render would re-create
  // the map, causing visible tile flicker.
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onMapClickRef = useRef(onMapClick);
  useEffect(() => { onBoundsChangeRef.current = onBoundsChange; }, [onBoundsChange]);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);

  useEffect(() => {
    let active = true;
    let mapInstance = null;
    // Snapshot refs at effect-run time so the cleanup uses the same Map
    // instances we attached to (satisfies react-hooks/exhaustive-deps).
    const capturedMarkers = markersRef;
    const capturedExpMarkers = experienceMarkersRef;
    const capturedUserMarker = userMarkerRef;

    prefetchAllureMapStyle(mapTheme);

    const initializeMap = () => {
      if (!mapCanvasRef.current || mapRef.current) return;
      mapInstance = new maplibregl.Map({
        container: mapCanvasRef.current,
        style: getInstantMapStyle(mapTheme),
        center: toLngLat(userPosition || defaultCenter),
        zoom: 13.8,
        attributionControl: false,
        cooperativeGestures: false,
        dragRotate: false,
        pitchWithRotate: false,
      });
      mapInstance.on('load', () => {
        if (!active) return;
        onBoundsChangeRef.current?.(getBoundsFromMap(mapInstance));
      });
      mapInstance.on('moveend', () => {
        if (!active) return;
        onBoundsChangeRef.current?.(getBoundsFromMap(mapInstance));
      });
      mapInstance.on('click', () => {
        if (active) onMapClickRef.current?.();
      });
      mapRef.current = mapInstance;
      // Dev-only hook to let headless tests measure pin pixel stability
      // (map.project(lngLat)) across zoom frames. Guarded by NODE_ENV so
      // we never ship a global map handle to real users.
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
        window.__allureGuestMap = mapInstance;
      }

      // Async: swap to the real themed tile style once it arrives.
      getAllureMapStyle(mapTheme).then((realStyle) => {
        if (!active || !mapRef.current) return;
        mapRef.current.setStyle(realStyle);
      }).catch(() => { /* keep instant style — markers still work */ });
    };
    initializeMap();

    return () => {
      active = false;
      capturedMarkers?.current?.forEach((marker) => marker.remove());
      capturedMarkers?.current?.clear();
      capturedExpMarkers?.current?.forEach((marker) => marker.remove());
      capturedExpMarkers?.current?.clear();
      capturedUserMarker?.current?.remove();
      if (capturedUserMarker) capturedUserMarker.current = null;
      if (mapInstance) mapInstance.remove();
      mapRef.current = null;
    };
    // One-time init: empty deps + Ref pattern for handlers. mapTheme
    // is picked up by the applyStyle effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Theme swap: apply the real style when the user toggles dark mode.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapRef.current) return;
      const style = await getAllureMapStyle(mapTheme);
      if (cancelled || !mapRef.current) return;
      mapRef.current.setStyle(style);
    })();
    return () => { cancelled = true; };
  }, [mapTheme]);

  // First-time recentre: when the geolocation permission resolves AFTER
  // the map has already initialised on the fallback centre, smoothly fly
  // to the user's actual position. Runs once per session.
  const didCenterOnUserRef = useRef(false);
  useEffect(() => {
    if (didCenterOnUserRef.current) return;
    if (locationStatus !== 'live') return;
    if (!userPosition || !mapRef.current) return;
    didCenterOnUserRef.current = true;
    mapRef.current.flyTo({
      center: toLngLat(userPosition),
      zoom: Math.max(mapRef.current.getZoom(), 13.8),
      duration: 1400,
      essential: true,
    });
  }, [locationStatus, userPosition, toLngLat]);

  const centerOnMe = useCallback(async () => {
    // If we're on the fallback location — either denied first time or
    // the initial fix hadn't landed — treat the tap as a manual "please
    // try again" and re-request the runtime permission. On Capacitor
    // this triggers a fresh Android system dialog; on web it retriggers
    // the browser prompt.
    if (locationStatus !== 'live') {
      try { await requestLocation(); } catch { /* keep map interactive */ }
    }
    if (userPosition && mapRef.current) {
      mapRef.current.easeTo({
        center: toLngLat(userPosition),
        zoom: Math.max(mapRef.current.getZoom(), 15.4),
        duration: 850,
        easing: (t) => 1 - ((1 - t) ** 3),
      });
    }
  }, [locationStatus, requestLocation, userPosition, toLngLat]);

  return { mapRef, centerOnMe, toLngLat };
}
